import { makeAutoObservable, runInAction } from "mobx";

/**
 * Manages websocket channels and service subscriptions.
 * Follows Single Responsibility Principle by focusing only on channel management.
 */
export class ChannelManager {
    // Observable state
    lastSetChannels = "";
    private debugMode = false;

    constructor(debugMode: boolean = false) {
        this.debugMode = debugMode;
        makeAutoObservable(this);
    }

    /**
     * Sets the last channels that were configured
     * @param channels - Comma-separated string of channel names
     */
    setLastSetChannels(channels: string): void {
        runInAction(() => {
            this.lastSetChannels = channels;
        });
    }

    /**
     * Parses service name from a channel string
     * @param channel - Channel string or array to parse
     * @returns Parsed service name or empty string
     */
    parseServiceFromChannel(channel: string | string[]): string {
        if (Array.isArray(channel)) {
            return channel.length > 0 ? this.parseServiceFromChannel(channel[0]) : "";
        }

        if (typeof channel !== "string") {
            return "";
        }

        // Extract service name from channel path
        // Example: "projects/123" -> "projects"
        const parts = channel.split("/");
        return parts[0] || "";
    }

    /**
     * Finds what channel updates are needed based on current channels and params
     * @param channels - Array of channel names to check
     * @param params - Route parameters that might affect channels
     * @returns Array of services that need channel updates
     */
    findChannelUpdates(
        channels: string[] | undefined,
        params: { jobNumber?: string }
    ): string[] {
        if (!channels || !Array.isArray(channels)) {
            return [];
        }

        const services: string[] = [];

        for (const channel of channels) {
            const service = this.parseServiceFromChannel(channel);
            if (service && !services.includes(service)) {
                services.push(service);
            }
        }

        // Add job-specific channels if jobNumber is present
        if (params.jobNumber) {
            const jobChannels = [
                "jobs",
                "production-orders",
                "schedules"
            ];

            for (const jobChannel of jobChannels) {
                if (!services.includes(jobChannel)) {
                    services.push(jobChannel);
                }
            }
        }

        return services;
    }

    /**
     * Converts a full path to a permission-based path for channel subscriptions
     * @param fullPath - Full URL path
     * @returns Permission path suitable for channel subscriptions
     */
    convertFullPathToPermissionPath(fullPath: string | string[]): string {
        if (Array.isArray(fullPath)) {
            return fullPath.length > 0 ? this.convertFullPathToPermissionPath(fullPath[0]) : "";
        }

        if (typeof fullPath !== "string") {
            return "";
        }

        // Remove query parameters and hash
        let path = fullPath.split("?")[0].split("#")[0];

        // Remove dynamic parameters (like IDs) to get permission path
        // Example: "/projects/123/details" -> "/projects/details"
        const parts = path.split("/").filter(part => part.length > 0);
        const permissionParts: string[] = [];

        for (const part of parts) {
            // Skip numeric IDs and UUID-like strings
            if (!/^\d+$/.test(part) && !/^[a-f0-9-]{36}$/i.test(part)) {
                permissionParts.push(part);
            }
        }

        return "/" + permissionParts.join("/");
    }

    /**
     * Logs channel operations in debug mode
     * @param operation - Description of the channel operation
     * @param data - Additional data to log
     */
    private debugLog(operation: string, data?: unknown): void {
        if (this.debugMode) {
            console.log(`[ChannelManager] ${operation}`, data);
        }
    }

    /**
     * Sets up route-specific channels
     * This method would typically interact with a websocket service
     */
    setRouteChannels(): void {
        this.debugLog("Setting route channels");
        // Implementation would depend on the websocket service being used
    }

    /**
     * Clears all route-specific channels
     * This method would typically interact with a websocket service
     */
    clearRouteChannels(): void {
        this.debugLog("Clearing route channels");
        // Implementation would depend on the websocket service being used
    }

    /**
     * Gets the current state of channel management for debugging
     * @returns Object containing current channel state
     */
    getChannelState(): { lastSetChannels: string } {
        return {
            lastSetChannels: this.lastSetChannels
        };
    }
}
